import {
  Crosshair,
  Shield,
  Trophy,
  Users,
  Building2,
  ClipboardList,
  Flag,
  Store,
  GraduationCap,
  ShieldCheck,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { UserRole } from '@/types/profile'
import type { CommitteeRole } from '@/types/clubs'

export interface ModuleLink {
  label: string
  path: string
}

export interface BrandTheme {
  gradient: string
  darkGlow: string
  descText: string
  shadow: string
}

export const brandThemes: BrandTheme[] = [
  {
    gradient: 'from-violet-600 to-purple-700',
    darkGlow: 'bg-violet-500/20',
    descText: 'text-violet-100/80',
    shadow: 'hover:shadow-violet-200/40 dark:hover:shadow-violet-500/10',
  },
  {
    gradient: 'from-amber-500 to-orange-600',
    darkGlow: 'bg-amber-500/20',
    descText: 'text-amber-100/80',
    shadow: 'hover:shadow-amber-200/40 dark:hover:shadow-amber-500/10',
  },
  {
    gradient: 'from-blue-500 to-indigo-600',
    darkGlow: 'bg-blue-500/20',
    descText: 'text-blue-100/80',
    shadow: 'hover:shadow-blue-200/40 dark:hover:shadow-blue-500/10',
  },
  {
    gradient: 'from-emerald-500 to-teal-600',
    darkGlow: 'bg-emerald-500/20',
    descText: 'text-emerald-100/80',
    shadow: 'hover:shadow-emerald-200/40 dark:hover:shadow-emerald-500/10',
  },
  {
    gradient: 'from-indigo-500 to-violet-600',
    darkGlow: 'bg-indigo-500/20',
    descText: 'text-indigo-100/80',
    shadow: 'hover:shadow-indigo-200/40 dark:hover:shadow-indigo-500/10',
  },
  {
    gradient: 'from-rose-500 to-pink-600',
    darkGlow: 'bg-rose-500/20',
    descText: 'text-rose-100/80',
    shadow: 'hover:shadow-rose-200/40 dark:hover:shadow-rose-500/10',
  },
  {
    gradient: 'from-cyan-500 to-blue-600',
    darkGlow: 'bg-cyan-500/20',
    descText: 'text-cyan-100/80',
    shadow: 'hover:shadow-cyan-200/40 dark:hover:shadow-cyan-500/10',
  },
  {
    gradient: 'from-orange-500 to-red-600',
    darkGlow: 'bg-orange-500/20',
    descText: 'text-orange-100/80',
    shadow: 'hover:shadow-orange-200/40 dark:hover:shadow-orange-500/10',
  },
  {
    gradient: 'from-teal-500 to-emerald-600',
    darkGlow: 'bg-teal-500/20',
    descText: 'text-teal-100/80',
    shadow: 'hover:shadow-teal-200/40 dark:hover:shadow-teal-500/10',
  },
  {
    gradient: 'from-red-500 to-rose-700',
    darkGlow: 'bg-red-500/20',
    descText: 'text-red-100/80',
    shadow: 'hover:shadow-red-200/40 dark:hover:shadow-red-500/10',
  },
]

export interface LandingTileDefinition {
  id: string
  slug: string
  title: string
  description: string
  icon: LucideIcon
  accentBg: string
  iconColor: string
  theme: BrandTheme
  links: ModuleLink[]
  visibility: {
    universal?: boolean
    roles?: UserRole[]
    committeeRoles?: CommitteeRole[]
    hasAnyCommitteeRole?: boolean
  }
  order: number
}

export const landingTiles: LandingTileDefinition[] = [
  {
    id: 'my-shooting',
    slug: 'my-shooting',
    title: 'My Shooting',
    description: 'Log shoots, track scores and view your classifications.',
    icon: Crosshair,
    accentBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    theme: brandThemes[0]!,
    links: [
      { label: 'Shoot Log', path: '/shoot-log' },
      { label: 'Stats', path: '/stats' },
      { label: 'Classifications', path: '/classifications' },
      { label: 'Challenges', path: '/challenges' },
      { label: 'Chronograph', path: '/chronograph' },
      { label: 'Training', path: '/training' },
    ],
    visibility: { universal: true },
    order: 1,
  },
  {
    id: 'my-armoury',
    slug: 'my-armoury',
    title: 'My Armoury',
    description: 'Manage firearms, ammunition and certificates.',
    icon: Shield,
    accentBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    theme: brandThemes[1]!,
    links: [
      { label: 'Firearms', path: '/armoury' },
      { label: 'Ammunition', path: '/armoury?tab=ammunition' },
      { label: 'Handloads', path: '/handloads' },
      { label: 'Certificates', path: '/wallet' },
    ],
    visibility: { universal: true },
    order: 2,
  },
  {
    id: 'competitions',
    slug: 'competitions',
    title: 'Competitions',
    description: 'Discover events, enter competitions and view results.',
    icon: Trophy,
    accentBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    theme: brandThemes[2]!,
    links: [
      { label: 'Discover', path: '/discover' },
      { label: 'My Entries', path: '/competitions' },
      { label: 'Results', path: '/results' },
      { label: 'Score Challenges', path: '/score-challenges' },
      { label: 'Calendar', path: '/calendar' },
    ],
    visibility: { universal: true },
    order: 3,
  },
  {
    id: 'social',
    slug: 'social-community',
    title: 'Social & Community',
    description: 'Connect with teams, clubs and fellow shooters.',
    icon: Users,
    accentBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    theme: brandThemes[3]!,
    links: [
      { label: 'Teams', path: '/teams' },
      { label: 'Clubs', path: '/clubs' },
      { label: 'Messages', path: '/messages' },
      { label: 'Photos', path: '/photos' },
      { label: 'Polls', path: '/polls' },
    ],
    visibility: { universal: true },
    order: 4,
  },
  {
    id: 'club-mgmt',
    slug: 'club-management',
    title: 'Club Management',
    description: 'Administer your club, members and finances.',
    icon: Building2,
    accentBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    theme: brandThemes[4]!,
    links: [
      { label: 'Meetings', path: '/meetings' },
      { label: 'Documents', path: '/documents' },
      { label: 'Club Portal', path: '/club-portal' },
      { label: 'Waiting List', path: '/waiting-list' },
      { label: 'Finances', path: '/competition-finance' },
      { label: 'Training Admin', path: '/training-admin' },
    ],
    visibility: { roles: ['club_admin', 'system_admin'], hasAnyCommitteeRole: true },
    order: 5,
  },
  {
    id: 'comp-ops',
    slug: 'competition-operations',
    title: 'Competition Operations',
    description: 'Run competitions, squad shooters and manage range ops.',
    icon: ClipboardList,
    accentBg: 'bg-rose-100',
    iconColor: 'text-rose-600',
    theme: brandThemes[5]!,
    links: [
      { label: 'Squadding', path: '/squadding' },
      { label: 'RO Dashboard', path: '/ro-dashboard' },
      { label: 'Runbook', path: '/runbook' },
      { label: 'Briefings', path: '/briefings' },
      { label: 'DQ Tracking', path: '/dq-tracking' },
    ],
    visibility: { roles: ['competition_admin', 'range_admin', 'system_admin'] },
    order: 6,
  },
  {
    id: 'team-captain',
    slug: 'team-captain',
    title: 'Team Captain',
    description: 'Manage your teams, select squads and plan fixtures.',
    icon: Flag,
    accentBg: 'bg-cyan-100',
    iconColor: 'text-cyan-600',
    theme: brandThemes[6]!,
    links: [
      { label: 'My Teams', path: '/teams' },
      { label: 'Squad Selection', path: '/squadding' },
      { label: 'Calendar', path: '/calendar' },
    ],
    visibility: { roles: ['team_captain', 'system_admin'] },
    order: 7,
  },
  {
    id: 'dealer-hub',
    slug: 'dealer-hub',
    title: 'Dealer Hub',
    description: 'Manage your marketplace listings and buyer messages.',
    icon: Store,
    accentBg: 'bg-orange-100',
    iconColor: 'text-orange-600',
    theme: brandThemes[7]!,
    links: [
      { label: 'Marketplace', path: '/marketplace' },
      { label: 'Messages', path: '/messages' },
    ],
    visibility: { roles: ['rfd', 'system_admin'] },
    order: 8,
  },
  {
    id: 'cadets',
    slug: 'cadets-family',
    title: 'Cadets & Family',
    description: 'Safeguarding, cadet management and family accounts.',
    icon: GraduationCap,
    accentBg: 'bg-teal-100',
    iconColor: 'text-teal-600',
    theme: brandThemes[8]!,
    links: [
      { label: 'Cadets', path: '/cadets' },
      { label: 'Family', path: '/family' },
    ],
    visibility: { committeeRoles: ['SAFEGUARDING_OFFICER'] },
    order: 9,
  },
  {
    id: 'platform-admin',
    slug: 'platform-admin',
    title: 'Platform Admin',
    description: 'System administration, reports and platform settings.',
    icon: ShieldCheck,
    accentBg: 'bg-red-100',
    iconColor: 'text-red-600',
    theme: brandThemes[9]!,
    links: [
      { label: 'Admin Panel', path: '/admin' },
      { label: 'Reports', path: '/reports' },
      { label: 'Exports', path: '/exports' },
      { label: 'Custom Fields', path: '/custom-fields' },
      { label: 'Webhooks', path: '/webhooks' },
      { label: 'Support', path: '/support' },
    ],
    visibility: { roles: ['system_admin'] },
    order: 10,
  },
]
